// Styles for CardComparison go here.
import styled, { css } from 'styled-components';
import { baseVariables, toRem } from '@marriott/mi-ui-library';
import { GLOBAL_NAV_HEIGHT } from '../../modules/constants';

interface StyledStickyCardProps {
  $hasStickyNav?: boolean;
  $isStuck?: boolean;
}

const stickyHeaderTransition = ({ $hasStickyNav }: StyledStickyCardProps) => ($hasStickyNav ? 'top 0.4s ease' : 'none');

// When the header docks as a stuck sticky bar, flatten the rounded top corners
// that only make sense while the header sits at the top of the table.
const stuckHeaderRadiusOverride = ({ $isStuck }: StyledStickyCardProps) =>
  $isStuck &&
  css`
    .table-container.enhanced-table-container
      .table-header
      .data-section
      .comparison-slot-wrapper:last-child
      .cell-wrapper
      > div
      > div {
      border-radius: 0;
    }
    .table-header .enhanced-comparison-cell {
      border-radius: 0;
    }
    .sticky-header .data-cell,
    .table-header .data-row .comparison-slot-empty {
      border-radius: 0;
    }
  `;

export const StyledCardComparison = styled.div<StyledStickyCardProps>`
  border-radius: 0;
  @media ${baseVariables.mediaQuery.md} {
    border-radius: ${toRem(14)} ${toRem(14)} 0 0;
  }
  .table-container {
    box-shadow: 0 ${toRem(4)} ${toRem(20)} 0 rgba(0, 0, 0, 0.07);
    background-color: ${baseVariables.color.merch50};
    border-radius: 0;
    @media ${baseVariables.mediaQuery.md} {
      border-radius: ${toRem(14)} ${toRem(14)} 0 0;
    }

    .table-wrapper {
      border-radius: 0;
      @media ${baseVariables.mediaQuery.md} {
        border-radius: ${toRem(14)} ${toRem(14)} 0 0;
      }
      .accordion-wrapper {
        &:last-child {
          .accordion-container {
            border-bottom: none;
          }
        }
      }
      .data-row {
        background-color: ${baseVariables.color.merch50};
        border-top: ${toRem(1)} ${baseVariables.border.borderStyleDefault} #f0f0f0;
      }
      .data-section {
        border-top: ${toRem(1)} ${baseVariables.border.borderStyleDefault} #f0f0f0;
        border-right: ${toRem(1)} ${baseVariables.border.borderStyleDefault} #f0f0f0;
        @media ${baseVariables.mediaQuery.md} {
          border: none;
        }
      }
      .data-cell {
        border-left: ${toRem(1)} ${baseVariables.border.borderStyleDefault} #f0f0f0;
      }
      .category-cell {
        background-color: #f4f4f4;
        @media ${baseVariables.mediaQuery.md} {
          background-color: ${baseVariables.color.merch50};
        }
        border-left: none;
      }
      .table-header .category-cell {
        background-color: ${baseVariables.color.merch50};
      }
    }
  }
  .table-container.enhanced-table-container {
    .table-header .data-section .comparison-slot-wrapper:last-child {
      .cell-wrapper > div > div {
        @media ${baseVariables.mediaQuery.md} {
          border-radius: 0 ${toRem(14)} 0 0;
        }
      }
    }
    .table-header .data-row {
      border-top: none;
      background-color: ${baseVariables.color.merch50};
      border-radius: 0;
      @media ${baseVariables.mediaQuery.md} {
        border-radius: ${toRem(14)} ${toRem(14)} 0 0;
      }
    }
    .data-cell {
      &:last-child .badge {
        @media ${baseVariables.mediaQuery.md} {
          border-radius: 0 ${toRem(14)} 0 0;
        }
      }
    }
  }
  .data-section .comparison-slot-wrapper:last-child .cell-wrapper > div {
    border-radius: 0;
    @media ${baseVariables.mediaQuery.md} {
      border-radius: 0 ${toRem(14)} 0 0;
    }
  }
  .comparison-slot-wrapper {
    .data-cell {
      width: 100%;
    }
  }
  .comparison-slot-clear {
    align-items: center;
    border-radius: ${toRem(4)};
    background: ${baseVariables.color.neutral20};
    display: flex;
    justify-content: center;
    position: absolute;
    right: ${toRem(24)};
    top: ${toRem(24)};
    z-index: 2;
  }
  .comparison-slot-dropdown-wrapper {
    left: ${toRem(12)};
    position: absolute;
    right: ${toRem(12)};
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
  }
  .comparison-empty-slots {
    gap: ${toRem(12)};
    padding: ${toRem(24)};
  }
  .comparison-empty-slot {
    flex: 1 1 0;
  }
  .table-header .comparison-slot-wrapper--empty .cell-wrapper {
    min-height: ${toRem(180)};
  }
  .table-header .comparison-slot-wrapper--empty .cell-wrapper > div {
    visibility: hidden;
  }
  .table-header,
  .sticky-header {
    position: sticky;
    top: ${({ $hasStickyNav }: StyledStickyCardProps) => ($hasStickyNav ? toRem(GLOBAL_NAV_HEIGHT.mobile) : 0)};
    @media ${baseVariables.mediaQuery.sm} {
      top: ${({ $hasStickyNav }: StyledStickyCardProps) => ($hasStickyNav ? toRem(GLOBAL_NAV_HEIGHT.desktop) : 0)};
    }
    transition: ${stickyHeaderTransition};
    z-index: 1;
    border-bottom: ${toRem(1)} ${baseVariables.border.borderStyleDefault} ${baseVariables.color.neutral30};
    box-shadow: 0 ${toRem(4)} ${toRem(20)} 0 rgba(0, 0, 0, 0.07);
    border-radius: ${toRem(14)} ${toRem(14)} 0 0;
    .data-row,
    .data-cell {
      border-radius: 0;
      @media ${baseVariables.mediaQuery.md} {
        border-radius: ${toRem(14)} 0 0 0;
      }
      .comparison-slot-empty {
        border-radius: 0;
        @media ${baseVariables.mediaQuery.md} {
          border-radius: 0 ${toRem(14)} 0 0;
        }
      }
    }
    .category-cell .cell-wrapper {
      justify-content: center;
    }
  }
  .data-cell {
    &:last-child .badge {
      @media ${baseVariables.mediaQuery.md} {
        border-radius: 0 ${toRem(14)} 0 0;
      }
    }
  }
  .sticky-header {
    border-radius: ${toRem(14)} ${toRem(14)} 0 0;
    position: sticky;
  }
  @media (max-width: ${toRem(767)}) {
    .header-category.header-hidden,
    .platinum-status.header-hidden,
    .gold-status.header-hidden,
    .silver-status.header-hidden,
    .card-image.header-hidden,
    .vertical-card-image.header-hidden {
      max-height: 0;
      min-height: 0;
      margin: 0;
      padding: 0;
      border: 0;
      overflow: hidden;
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
    }
  }
  ${stuckHeaderRadiusOverride}
`;
